import React from 'react';

import clsx from 'clsx';

import {
  Card,
  CardContent,
  Stack,
  Checkbox,
  Typography,
  makeStyles,
} from '@gogame/components';

import { PreviewWithScrubber } from 'scenes/Gallery/components/File';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      position: 'relative',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      '&:hover': {
        boxShadow: ({ withHover }) =>
          withHover ? `${theme.shadows[5]}` : null,
      },
    },
    selected: {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
    },
    controls: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1,
      color: theme.palette.common.white,
      // filter:
      //   'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.14)) drop-shadow(0px 2px 1px rgba(0, 0, 0, 0.12)) drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.2))',
      pointerEvents: 'none',
    },
    checkbox: {
      color: theme.palette.common.white,
      pointerEvents: 'all',
    },
    actions: {
      pointerEvents: 'all',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      flex: '0 0 auto',
    },
    title: {
      wordBreak: 'break-all',
    },
  };
});

const defaultRenderPreview = ({ previewComponent }) => previewComponent;

const Tile = ({
  id,
  name,
  previewSrc,
  previewThumbnail,
  previewScrubbingUrls,
  heightCover,
  selected,
  onSelect,
  renderPreview = defaultRenderPreview,
  title,
  content,
  attributesPreview,
  attributes,
  attributesSecondary,
  actions,
  withLabel,
}) => {
  const classes = useStyles({
    withHover: !!onSelect,
  });

  const [hover, setHover] = React.useState();

  const handleHoverOn = React.useCallback(() => {
    setHover(true);
  }, [onSelect, setHover]);

  const handleHoverOff = React.useCallback(() => {
    setHover(false);
  }, [onSelect, setHover]);

  const hasContent = !!(title || content || attributes || attributesSecondary);

  const card = (
    <Card
      className={clsx(classes.root, {
        [classes.selected]: selected,
      })}
      onMouseOver={handleHoverOn}
      onMouseLeave={handleHoverOff}
      data-cy={'tile'}
      data-cy-id={id}
      data-cy-name={name}
    >
      {onSelect && (hover || selected) ? (
        <Stack
          className={classes.controls}
          justifyContent={'space-between'}
          direction={'row'}
          spacing={1}
        >
          <Checkbox
            className={classes.checkbox}
            checked={selected}
            onClick={onSelect}
          />
          <div className={classes.actions}>{actions}</div>
        </Stack>
      ) : null}
      <Stack flex={'1 1 auto'}>
        {renderPreview({
          previewComponent: (
            <PreviewWithScrubber
              src={previewSrc}
              thumbnail={previewThumbnail}
              scrubbingUrls={previewScrubbingUrls}
              width={'100%'}
              height={'auto'}
              heightCover={heightCover}
              aspectRatio={'59/31'}
              attributes={attributesPreview}
            />
          ),
        })}
      </Stack>
      {hasContent && (
        <CardContent className={classes.content}>
          <Stack flex={'1 1 auto'} spacing={1} justifyContent={'space-between'}>
            <Typography
              className={classes.title}
              variant={'body1'}
              lineClamp={2}
            >
              {title}
            </Typography>
            <Stack spacing={1}>
              <Typography
                variant={'caption'}
                color={'textSecondary'}
                noWrap={true}
              >
                {content}
              </Typography>
              <Stack direction={'row'} justifyContent={'space-between'}>
                {attributes}
                {attributesSecondary}
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      )}
    </Card>
  );

  return withLabel ? <label>{card}</label> : card;
};

export default Tile;
